import React, { useState, useRef } from 'react';
import axios from 'axios';
import {
  CheckCircle2,
  Clock,
  XCircle,
  Send,
  Loader2,
  ShieldCheck,
  Paperclip,
  Camera,
  X as XIcon,
  FileText as FileIcon,
  PlusCircle,
  MessageSquare,
  Building2,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_FILES = 5;
const MAX_BYTES = 12 * 1024 * 1024; // 12 MB (iPhone HEIC / high-res photos)

const BUREAUS = [
  { key: 'experian',   label: 'Experian',   accent: 'from-blue-500 to-indigo-500',    tint: 'blue' },
  { key: 'equifax',    label: 'Equifax',    accent: 'from-red-500 to-rose-500',       tint: 'rose' },
  { key: 'transunion', label: 'TransUnion', accent: 'from-emerald-500 to-teal-500',   tint: 'emerald' },
];

/**
 * ClientUpdatePage — public, standalone, no sidebar, no nav to app.
 *
 * Clients submit their dispute progress: accounts removed, pending, remaining,
 * optional reason for anything still remaining, optional 3-bureau scores, and
 * up to 5 photos of any documents they received from the bureaus or creditors.
 * On submit → backend saves everything + emails the app owner + shows a
 * thank-you view.  This page has NO links back into the app.
 */
export default function ClientUpdatePage() {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    removed: '',
    pending: '',
    remaining: '',
    newAccounts: '',
    requestRemoval: false,
    reason: '',
    additionalInfo: '',
    scoreExperian: '',
    scoreEquifax: '',
    scoreTransunion: '',
  });
  // Per-bureau status (Feb 2026 request):  each bureau has its own
  // removed / pending / remaining text so the owner can see exactly
  // what each bureau reported.  Kept OPTIONAL — clients who just want
  // the top-level fields can leave these blank.
  const [bureauStatus, setBureauStatus] = useState({
    experian:   { removed: '', pending: '', remaining: '' },
    equifax:    { removed: '', pending: '', remaining: '' },
    transunion: { removed: '', pending: '', remaining: '' },
  });
  // Optional question block at the bottom of the form.  If subject +
  // message are filled in, we fire a second POST to
  // /api/public/client-question so the owner gets emailed.
  const [question, setQuestion] = useState({ subject: '', message: '', email: '' });
  const [questionSending, setQuestionSending] = useState(false);
  const [questionSent, setQuestionSent] = useState(false);
  const [files, setFiles] = useState([]); // [{file, previewUrl, isPdf}]
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const splitLines = (s) =>
    (s || '')
      .split(/\n|,/)
      .map((x) => x.trim())
      .filter(Boolean);

  const onPickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const combined = [...files];
    for (const f of picked) {
      if (combined.length >= MAX_FILES) break;
      if (f.size > MAX_BYTES) {
        setError(`"${f.name}" is too large (max 12 MB).`);
        continue;
      }
      const isPdf = f.type === 'application/pdf';
      const previewUrl = isPdf ? null : URL.createObjectURL(f);
      combined.push({ file: f, previewUrl, isPdf });
    }
    setFiles(combined);
    setError('');
    // reset the input so picking the same file again still fires onChange
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (idx) => {
    const next = [...files];
    if (next[idx]?.previewUrl) URL.revokeObjectURL(next[idx].previewUrl);
    next.splice(idx, 1);
    setFiles(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError('First and last name are required.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('firstName', form.firstName.trim());
      fd.append('lastName', form.lastName.trim());
      fd.append('accountsRemoved', JSON.stringify(splitLines(form.removed)));
      fd.append('accountsPending', JSON.stringify(splitLines(form.pending)));
      fd.append('accountsRemaining', JSON.stringify(splitLines(form.remaining)));
      fd.append('accountsNew', JSON.stringify(splitLines(form.newAccounts)));
      // Serialize the per-bureau status blob to JSON so the backend
      // can store it as a nested dict.
      fd.append('bureauStatus', JSON.stringify({
        experian:   { removed: splitLines(bureauStatus.experian.removed),   pending: splitLines(bureauStatus.experian.pending),   remaining: splitLines(bureauStatus.experian.remaining) },
        equifax:    { removed: splitLines(bureauStatus.equifax.removed),    pending: splitLines(bureauStatus.equifax.pending),    remaining: splitLines(bureauStatus.equifax.remaining) },
        transunion: { removed: splitLines(bureauStatus.transunion.removed), pending: splitLines(bureauStatus.transunion.pending), remaining: splitLines(bureauStatus.transunion.remaining) },
      }));
      // Only send requestRemoval=true when the client actually has NEW
      // accounts listed AND ticked the box — protects against a stale
      // checkbox after they cleared the textarea.
      const newList = splitLines(form.newAccounts);
      fd.append('requestRemoval', (form.requestRemoval && newList.length > 0) ? 'true' : 'false');
      fd.append('reasonRemaining', form.reason.trim());
      fd.append('additionalInfo', form.additionalInfo.trim());
      if (form.scoreExperian) fd.append('scoreExperian', String(parseInt(form.scoreExperian, 10)));
      if (form.scoreEquifax) fd.append('scoreEquifax', String(parseInt(form.scoreEquifax, 10)));
      if (form.scoreTransunion) fd.append('scoreTransunion', String(parseInt(form.scoreTransunion, 10)));
      files.forEach(({ file }) => fd.append('attachments', file, file.name));

      await axios.post(`${API_URL}/api/public/client-update`, fd, {
        timeout: 60000,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitQuestion = async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setError('Please enter your first and last name at the top before sending a question.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!question.subject.trim() || !question.message.trim()) {
      setError('Question needs a subject and a message.');
      return;
    }
    setError('');
    setQuestionSending(true);
    try {
      await axios.post(`${API_URL}/api/public/client-question`, {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: question.email.trim(),
        subject: question.subject.trim(),
        message: question.message.trim(),
      }, { timeout: 20000 });
      setQuestionSent(true);
      setQuestion({ subject: '', message: '', email: '' });
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not send your question. Please try again.');
    } finally {
      setQuestionSending(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-teal-950 to-slate-900 flex items-center justify-center px-4 py-16 text-white">
        <div
          className="max-w-lg w-full bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 md:p-10 text-center shadow-2xl"
          data-testid="client-update-thankyou"
        >
          <div className="w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="w-9 h-9 text-emerald-400" />
          </div>
          <h1 className="text-2xl font-bold mb-3">Thank you, {form.firstName}!</h1>
          <p className="text-slate-300 text-sm leading-relaxed">
            Your update has been received. I&apos;ll review it shortly and follow up if I need
            anything else. Keep up the great work checking in — your updates are what move
            your case forward.
          </p>
          <p className="text-xs text-slate-500 mt-6">You can close this window.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-teal-950 to-slate-900 text-white">
      <div className="max-w-3xl mx-auto px-4 py-8 md:py-14">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-teal-500/15 border border-teal-400/30 text-teal-300 text-xs font-semibold uppercase tracking-wider mb-3">
            <ShieldCheck className="w-3.5 h-3.5" />
            Secure Client Update
          </div>
          <h1 className="text-3xl md:text-4xl font-bold" data-testid="client-update-title">
            Credit Dispute Status Update
          </h1>
          <p className="text-slate-400 text-sm md:text-base mt-3 max-w-xl mx-auto leading-relaxed">
            Let me know what the bureaus told you this week — which accounts were removed,
            which are still pending, and which are still on your report. This is how I know
            what&apos;s happening on your file so I can push the next round.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 md:p-8 space-y-6 shadow-2xl"
          data-testid="client-update-form"
        >
          {/* Name row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="First Name *" value={form.firstName} onChange={set('firstName')} testId="cu-first-name" required />
            <Field label="Last Name *" value={form.lastName} onChange={set('lastName')} testId="cu-last-name" required />
          </div>

          {/* ================================================================
              PER-BUREAU STATUS (Feb 2026 — user request).
              PROMOTED to primary input Feb 2026 so admin's email + inbox
              always show accounts grouped by bureau.  Cards default-open
              on first render so clients see the 3 bureau buckets right
              away.
              ================================================================ */}
          <div className="bg-gradient-to-br from-teal-900/30 via-slate-900/40 to-cyan-900/30 border-2 border-teal-400/40 rounded-2xl p-5 shadow-xl shadow-teal-500/10" data-testid="cu-bureau-status-block">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center shrink-0 shadow-lg shadow-teal-500/40">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="text-base font-black text-white">Tell Me Per Bureau</label>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-teal-500/25 border border-teal-400/50 text-teal-100 text-[10px] font-bold uppercase tracking-wider">Primary</span>
                </div>
                <p className="text-xs text-slate-300 mt-1 leading-relaxed">
                  This is what I need most — list which accounts each bureau removed, is still investigating, or is still reporting.
                  Fill in every bureau you have news for; skip any you don't.
                </p>
              </div>
            </div>
            <div className="space-y-2">
              {BUREAUS.map((b, i) => (
                <BureauStatusCard
                  key={b.key}
                  bureau={b}
                  value={bureauStatus[b.key]}
                  defaultOpen={i === 0}
                  onChange={(field, val) =>
                    setBureauStatus((prev) => ({
                      ...prev,
                      [b.key]: { ...prev[b.key], [field]: val },
                    }))
                  }
                />
              ))}
            </div>
          </div>

          {/* ─── Fallback combined fields — only for clients who don't
                know which bureau reported what.  De-emphasized so
                Per-Bureau above is the natural default flow. ─── */}
          <details className="rounded-xl border border-white/10 bg-slate-900/30" data-testid="cu-flat-fields-details">
            <summary className="cursor-pointer px-4 py-3 text-xs uppercase tracking-wide text-slate-400 font-semibold hover:text-slate-200 select-none">
              ▸ Don't know which bureau? Tap to list them all together
            </summary>
            <div className="p-4 pt-2 space-y-4 border-t border-white/5">
              <TextareaField
                icon={<CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                label="Accounts REMOVED (combined)"
                hint="Only use this if you don't know which bureau — otherwise use the Per-Bureau block above."
                value={form.removed}
                onChange={set('removed')}
                testId="cu-removed"
              />
              <TextareaField
                icon={<Clock className="w-4 h-4 text-amber-400" />}
                label="Accounts PENDING (combined)"
                hint="Only use this if you don't know which bureau."
                value={form.pending}
                onChange={set('pending')}
                testId="cu-pending"
              />
              <TextareaField
                icon={<XCircle className="w-4 h-4 text-rose-400" />}
                label="Accounts REMAINING (combined)"
                hint="Only use this if you don't know which bureau."
                value={form.remaining}
                onChange={set('remaining')}
                testId="cu-remaining"
              />
            </div>
          </details>

          <TextareaField
            icon={<PlusCircle className="w-4 h-4 text-amber-300" />}
            label="🆕 NEW Accounts Added to File"
            hint="Any brand-new accounts that showed up on your credit file since the last update — this helps me flag possible identity theft or unauthorized items immediately."
            value={form.newAccounts}
            onChange={set('newAccounts')}
            testId="cu-new-accounts"
          />

          {/* Removal request panel — only shown when the client has listed
              new accounts.  Explains the pricing, lets them opt in with a
              checkbox, then reveals the payment methods. */}
          <NewAccountRemovalPanel
            newAccountsRaw={form.newAccounts}
            checked={form.requestRemoval}
            onToggle={(v) => setForm({ ...form, requestRemoval: v })}
            splitLines={splitLines}
          />

          <div>
            <label className="text-sm font-semibold text-slate-200 mb-1.5 block">
              Why did they remain?{' '}
              <span className="text-slate-500 font-normal">(optional)</span>
            </label>
            <textarea
              value={form.reason}
              onChange={set('reason')}
              rows={3}
              placeholder="Anything the bureau told you about why an account stayed..."
              className="w-full bg-slate-900/60 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-teal-400/50 text-sm"
              data-testid="cu-reason"
            />
          </div>

          <div>
            <label className="text-sm font-semibold text-slate-200 mb-1.5 block">
              Credit Scores{' '}
              <span className="text-slate-500 font-normal">(optional — fill in what you know)</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <ScoreField label="Experian"  value={form.scoreExperian}   onChange={set('scoreExperian')}   testId="cu-score-experian" />
              <ScoreField label="Equifax"   value={form.scoreEquifax}    onChange={set('scoreEquifax')}    testId="cu-score-equifax" />
              <ScoreField label="TransUnion" value={form.scoreTransunion} onChange={set('scoreTransunion')} testId="cu-score-transunion" />
            </div>
          </div>

          {/* Attachments — new */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-200 mb-1.5">
              <Paperclip className="w-4 h-4 text-teal-300" /> Attach Documents{' '}
              <span className="text-slate-500 font-normal">(optional — up to 5 photos or PDFs)</span>
            </label>
            <p className="text-[11px] text-slate-500 mb-2">
              Snap a photo of any letters you got from the credit bureaus or creditors. Images
              and PDFs, 8&nbsp;MB max each.
            </p>
            {/* Thumbnails */}
            {files.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-3" data-testid="cu-attachment-thumbs">
                {files.map((f, idx) => (
                  <div
                    key={idx}
                    className="relative aspect-square rounded-lg overflow-hidden border border-white/15 bg-slate-900"
                    data-testid={`cu-thumb-${idx}`}
                  >
                    {f.isPdf ? (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-slate-400 p-1">
                        <FileIcon className="w-6 h-6" />
                        <span className="text-[9px] text-center truncate w-full px-1">
                          {f.file.name}
                        </span>
                      </div>
                    ) : (
                      <img
                        src={f.previewUrl}
                        alt={f.file.name}
                        className="w-full h-full object-cover"
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => removeFile(idx)}
                      className="absolute top-1 right-1 bg-black/70 hover:bg-red-500 rounded-full p-0.5 transition"
                      data-testid={`cu-remove-thumb-${idx}`}
                      aria-label="Remove"
                    >
                      <XIcon className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {/* Picker button */}
            {files.length < MAX_FILES && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full inline-flex items-center justify-center gap-2 border-2 border-dashed border-teal-400/40 hover:border-teal-400/70 hover:bg-teal-400/5 text-teal-200 rounded-lg py-3 transition text-sm font-medium"
                data-testid="cu-add-attachment"
              >
                <Camera className="w-4 h-4" />
                {files.length === 0 ? 'Add photo or PDF' : `Add another (${files.length}/${MAX_FILES})`}
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf,.heic,.heif"
              multiple
              onChange={onPickFiles}
              className="hidden"
              data-testid="cu-file-input"
            />
            {files.length === MAX_FILES && (
              <p className="text-xs text-teal-300 mt-2 text-center">
                Maximum of {MAX_FILES} attachments reached.
              </p>
            )}
          </div>

          {/* Additional Information — free-form catch-all at the very bottom */}
          <div>
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-200 mb-1.5">
              💬 Additional Information{' '}
              <span className="text-slate-500 font-normal">(optional)</span>
            </label>
            <textarea
              value={form.additionalInfo}
              onChange={set('additionalInfo')}
              rows={4}
              placeholder="Anything else you want to let me know? Concerns, questions, life updates, calls the bureau made to you — anything at all..."
              className="w-full bg-slate-900/60 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-teal-400/50 text-sm"
              data-testid="cu-additional-info"
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Use this space for anything not covered above — I read every submission.
            </p>
          </div>

          {error && (
            <div
              className="text-rose-300 text-sm bg-rose-500/10 border border-rose-500/30 rounded-lg px-4 py-2.5"
              data-testid="cu-error"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-all shadow-lg shadow-teal-500/20"
            data-testid="cu-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" /> Submit Update
              </>
            )}
          </button>

          <p className="text-[11px] text-slate-500 text-center leading-relaxed">
            This form is private. Only your credit consultant will see what you submit.
          </p>
        </form>

        {/* ================================================================
            ASK A QUESTION — separate submission (doesn't require filling
            the status form above).  Sends an email to the consultant
            AND shows in their in-app inbox.
            ================================================================ */}
        <div
          className="mt-6 bg-white/5 backdrop-blur-sm border border-cyan-400/25 rounded-2xl p-6 md:p-8 shadow-2xl"
          data-testid="cu-question-block"
        >
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/15 border border-cyan-400/30 flex items-center justify-center flex-shrink-0">
              <MessageSquare className="w-5 h-5 text-cyan-300" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Have a question? Ask me directly</h2>
              <p className="text-[13px] text-slate-400 mt-0.5 leading-relaxed">
                I&apos;ll get your message in my inbox right away and reply straight to your email.
                Great for anything from &quot;when&apos;s my next round?&quot; to
                &quot;the bureau called me — what should I do?&quot;.
              </p>
            </div>
          </div>

          {questionSent ? (
            <div className="rounded-lg bg-emerald-500/10 border border-emerald-400/30 p-4 text-emerald-200 text-sm flex items-start gap-2" data-testid="cu-question-sent">
              <CheckCircle2 className="w-5 h-5 text-emerald-300 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold">Question sent!</div>
                <div className="text-[12px] text-emerald-100/80 mt-0.5">I&apos;ll reply to you by email as soon as I can — usually within a business day.</div>
                <button
                  type="button"
                  onClick={() => setQuestionSent(false)}
                  className="mt-2 text-[12px] underline text-cyan-300 hover:text-cyan-200"
                  data-testid="cu-question-send-another"
                >
                  Send another question
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-300 mb-1 block">Your email <span className="text-slate-500 font-normal">(optional — I&apos;ll use the one I have on file if blank)</span></label>
                <input
                  type="email"
                  value={question.email}
                  onChange={(e) => setQuestion({ ...question, email: e.target.value })}
                  placeholder="you@example.com"
                  className="w-full bg-slate-900/60 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 text-sm"
                  data-testid="cu-question-email"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-300 mb-1 block">Subject *</label>
                <input
                  type="text"
                  value={question.subject}
                  onChange={(e) => setQuestion({ ...question, subject: e.target.value })}
                  placeholder="e.g., When will Capital One come off my Experian?"
                  className="w-full bg-slate-900/60 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 text-sm"
                  data-testid="cu-question-subject"
                  maxLength={200}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-300 mb-1 block">Message *</label>
                <textarea
                  value={question.message}
                  onChange={(e) => setQuestion({ ...question, message: e.target.value })}
                  rows={4}
                  placeholder="Type your question here…"
                  className="w-full bg-slate-900/60 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/50 text-sm"
                  data-testid="cu-question-message"
                  maxLength={5000}
                />
                <div className="text-[10px] text-slate-500 text-right mt-1">
                  {question.message.length} / 5000
                </div>
              </div>
              <button
                type="button"
                onClick={submitQuestion}
                disabled={questionSending}
                className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-lg transition-all shadow-lg shadow-cyan-500/20"
                data-testid="cu-question-submit"
              >
                {questionSending ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
                ) : (
                  <><Send className="w-4 h-4" /> Send Question</>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Collapsible per-bureau status card.  Header always visible; body
// (three textareas) expands on click.  Purely visual — the parent
// still owns the state.
function BureauStatusCard({ bureau, value, onChange, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const filled =
    (value.removed || '').trim() ||
    (value.pending || '').trim() ||
    (value.remaining || '').trim();
  return (
    <div
      className={`rounded-lg border transition-colors ${
        open || filled
          ? 'bg-slate-900/70 border-white/20'
          : 'bg-slate-900/40 border-white/10 hover:border-white/20'
      }`}
      data-testid={`cu-bureau-card-${bureau.key}`}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        data-testid={`cu-bureau-toggle-${bureau.key}`}
      >
        <div className="flex items-center gap-2.5">
          <div className={`w-2 h-8 rounded-full bg-gradient-to-b ${bureau.accent}`} />
          <div>
            <div className="text-sm font-semibold text-white">{bureau.label}</div>
            <div className="text-[10px] text-slate-400 uppercase tracking-wide">
              {filled ? 'Filled in ✓' : 'Tap to add updates'}
            </div>
          </div>
        </div>
        <span className={`text-slate-400 text-lg transition-transform ${open ? 'rotate-90' : ''}`}>›</span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-3 border-t border-white/5">
          <div>
            <label className="flex items-center gap-1.5 text-[11px] font-semibold text-emerald-300 mb-1 mt-2">
              <CheckCircle2 className="w-3.5 h-3.5" /> Removed at {bureau.label}
            </label>
            <textarea
              value={value.removed}
              onChange={(e) => onChange('removed', e.target.value)}
              rows={2}
              placeholder="One per line — Capital One 4111, Chase 5500…"
              className="w-full bg-slate-950/60 border border-white/10 rounded-md px-3 py-2 text-white placeholder-slate-600 focus:outline-none focus:border-emerald-400/40 text-[13px]"
              data-testid={`cu-bureau-removed-${bureau.key}`}
            />
          </div>
          <div>
            <label className="flex items-center gap-1.5 text-[11px] font-semibold text-amber-300 mb-1">
              <Clock className="w-3.5 h-3.5" /> Pending at {bureau.label}
            </label>
            <textarea
              value={value.pending}
              onChange={(e) => onChange('pending', e.target.value)}
              rows={2}
              placeholder="Accounts they&apos;re still investigating…"
              className="w-full bg-slate-950/60 border border-white/10 rounded-md px-3 py-2 text-white placeholder-slate-600 focus:outline-none focus:border-amber-400/40 text-[13px]"
              data-testid={`cu-bureau-pending-${bureau.key}`}
            />
          </div>
          <div>
            <label className="flex items-center gap-1.5 text-[11px] font-semibold text-rose-300 mb-1">
              <XCircle className="w-3.5 h-3.5" /> Remaining at {bureau.label}
            </label>
            <textarea
              value={value.remaining}
              onChange={(e) => onChange('remaining', e.target.value)}
              rows={2}
              placeholder="Accounts still on your report at this bureau…"
              className="w-full bg-slate-950/60 border border-white/10 rounded-md px-3 py-2 text-white placeholder-slate-600 focus:outline-none focus:border-rose-400/40 text-[13px]"
              data-testid={`cu-bureau-remaining-${bureau.key}`}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testId, required }) {
  return (
    <div>
      <label className="text-sm font-semibold text-slate-200 mb-1.5 block">{label}</label>
      <input
        type="text"
        value={value}
        onChange={onChange}
        required={required}
        className="w-full bg-slate-900/60 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-teal-400/50 text-sm"
        data-testid={testId}
      />
    </div>
  );
}

function TextareaField({ icon, label, hint, value, onChange, testId }) {
  return (
    <div>
      <label className="flex items-center gap-2 text-sm font-semibold text-slate-200 mb-1.5">
        {icon} {label}
      </label>
      <textarea
        value={value}
        onChange={onChange}
        rows={3}
        placeholder="One per line…"
        className="w-full bg-slate-900/60 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-teal-400/50 text-sm font-mono"
        data-testid={testId}
      />
      <p className="text-[11px] text-slate-500 mt-1">{hint}</p>
    </div>
  );
}

function ScoreField({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-xs text-slate-400 mb-1 block">{label}</label>
      <input
        type="number"
        min="300"
        max="850"
        value={value}
        onChange={onChange}
        placeholder="e.g. 650"
        className="w-full bg-slate-900/60 border border-white/10 rounded-lg px-3 py-2 text-white placeholder-slate-500 focus:outline-none focus:border-teal-400/50 text-sm"
        data-testid={testId}
      />
    </div>
  );
}

/**
 * Removal-request panel — shown BENEATH the "New Accounts Added" textarea.
 * Only visible when the client actually types something in that field.
 * Shows dynamic pricing ($150 paperwork + $100/account), a checkbox to opt
 * in, and — once ticked — payment instructions with all 5 methods.
 */
function NewAccountRemovalPanel({ newAccountsRaw, checked, onToggle, splitLines }) {
  const accounts = splitLines(newAccountsRaw);
  const n = accounts.length;
  if (n === 0) return null;
  const fee = 150 + 100 * n;

  return (
    <div
      className="rounded-xl border-2 border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 to-cyan-500/5 p-5 space-y-4"
      data-testid="cu-removal-panel"
    >
      <div>
        <div className="text-xs uppercase tracking-wider text-emerald-300 font-bold mb-1">
          💰 Optional — Want me to remove these {n === 1 ? 'account' : 'accounts'}?
        </div>
        <p className="text-sm text-slate-200 leading-relaxed">
          If you&apos;d like me to file the paperwork to remove the {n === 1 ? 'new account' : `${n} new accounts`} you listed above,
          the cost is <strong>$150 for the paperwork</strong> plus <strong>$100 per account</strong>.
        </p>
      </div>

      {/* Live price breakdown */}
      <div className="bg-black/30 rounded-lg p-3 grid grid-cols-3 gap-2 text-center border border-white/10">
        <div>
          <div className="text-[10px] uppercase text-slate-400">Paperwork</div>
          <div className="text-white font-bold text-lg">$150</div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-slate-400">
            {n} × $100
          </div>
          <div className="text-white font-bold text-lg">${n * 100}</div>
        </div>
        <div className="border-l border-emerald-500/30 pl-2">
          <div className="text-[10px] uppercase text-emerald-300 font-semibold">Total</div>
          <div className="text-emerald-300 font-black text-lg" data-testid="cu-removal-total">
            ${fee}
          </div>
        </div>
      </div>

      {/* Opt-in checkbox */}
      <label
        className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition border-2 ${
          checked
            ? 'bg-emerald-500/20 border-emerald-400'
            : 'bg-slate-900/40 border-white/10 hover:border-emerald-400/40'
        }`}
        data-testid="cu-removal-checkbox-label"
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onToggle(e.target.checked)}
          className="mt-0.5 w-5 h-5 rounded accent-emerald-500 flex-shrink-0"
          data-testid="cu-removal-checkbox"
        />
        <div>
          <div className="text-sm font-semibold text-white">
            ✓ Yes, please remove {n === 1 ? 'this account' : 'these accounts'} for me
          </div>
          <div className="text-[11px] text-slate-300 mt-0.5">
            My consultant will be notified and will follow up with next steps.
          </div>
        </div>
      </label>

      {/* Payment details — only revealed when checked */}
      {checked && (
        <div
          className="rounded-lg bg-slate-950/70 border border-emerald-500/30 p-4 space-y-3"
          data-testid="cu-payment-info"
        >
          <div className="text-xs uppercase tracking-wider text-emerald-300 font-bold">
            💳 Payment Options
          </div>
          <p className="text-sm text-slate-200 leading-relaxed">
            You can pay <strong className="text-emerald-300">FREE</strong> through <strong>Apple Pay</strong>, <strong>Zelle</strong>, or <strong>Venmo</strong>:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <PayMethod label="Zelle"    value="srsworldLLC@gmail.com"     free />
            <PayMethod label="Apple Pay" value="619-253-9785"             free />
            <PayMethod label="Venmo"    value="Clearall-Creditrepairs-1"  free />
            <PayMethod label="PayPal"   value="mrhunbun1@gmail.com"       fee />
            <PayMethod label="Cash App" value="$CLEARALLCR"               fee />
          </div>
          <div className="text-[12px] leading-relaxed bg-amber-500/10 border border-amber-500/30 rounded-md p-3 text-amber-100">
            ⚠️ <strong>Cash App and PayPal charge a fee</strong> — please add <strong>$3 per $100</strong> you send to cover it.
            <br />
            <span className="text-amber-200 mt-1 inline-block">
              For your ${fee} total on Cash App/PayPal, send <strong>${fee + Math.ceil(fee / 100) * 3}</strong>.
            </span>
          </div>
          <div className="text-[12px] leading-relaxed bg-blue-500/10 border border-blue-500/30 rounded-md p-3 text-blue-100">
            📸 After you pay, please <strong>screenshot the payment confirmation</strong> and text it to me with the message
            &quot;<em>This is for a new account removal</em>&quot; so I can match it to your file and start right away.
          </div>
        </div>
      )}
    </div>
  );
}

function PayMethod({ label, value, free, fee }) {
  return (
    <div className="bg-slate-900/60 border border-white/10 rounded-md px-3 py-2 flex items-center justify-between gap-2">
      <div className="min-w-0">
        <div className="text-[10px] uppercase text-slate-400 tracking-wide">{label}</div>
        <div className="text-white font-mono text-xs truncate">{value}</div>
      </div>
      {free && (
        <span className="text-[9px] font-bold text-emerald-300 bg-emerald-500/20 px-1.5 py-0.5 rounded uppercase tracking-wider whitespace-nowrap">
          Free
        </span>
      )}
      {fee && (
        <span className="text-[9px] font-bold text-amber-300 bg-amber-500/20 px-1.5 py-0.5 rounded uppercase tracking-wider whitespace-nowrap">
          +Fee
        </span>
      )}
    </div>
  );
}
